import { useState, useEffect, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { supportedLanguages, type SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { cn } from '@/lib/utils'

function ItalianFlag({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 18" className={className} aria-hidden="true">
      <rect x="0" width="8" height="18" fill="#009246" />
      <rect x="8" width="8" height="18" fill="#ffffff" />
      <rect x="16" width="8" height="18" fill="#ce2b37" />
    </svg>
  )
}

function BritishFlag({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 18" className={className} aria-hidden="true">
      <rect width="24" height="18" fill="#012169" />
      <path d="M0,0 L24,18 M24,0 L0,18" stroke="#ffffff" strokeWidth="3" />
      <path d="M0,0 L24,18 M24,0 L0,18" stroke="#C8102E" strokeWidth="2" />
      <path d="M12,0 V18 M0,9 H24" stroke="#ffffff" strokeWidth="5" />
      <path d="M12,0 V18 M0,9 H24" stroke="#C8102E" strokeWidth="3" />
    </svg>
  )
}

/**
 * Metadata for every language this selector knows how to render.
 * At runtime the dropdown is filtered down to the project's `supportedLanguages`,
 * so you can enable a new language simply by adding its code to
 * `src/i18n/index.ts` — as long as the metadata exists here the UI picks it up.
 */
interface LanguageMeta {
  code: string
  flag: ReactNode
  name: string
  short: string
}

const languagesMeta: LanguageMeta[] = [
  { code: 'it', flag: <ItalianFlag className="h-4 w-auto rounded-sm" />, name: 'Italiano', short: 'IT' },
  { code: 'en', flag: <BritishFlag className="h-4 w-auto rounded-sm" />, name: 'English', short: 'EN' },
  { code: 'fr', flag: <span>🇫🇷</span>, name: 'Français', short: 'FR' },
  { code: 'es', flag: <span>🇪🇸</span>, name: 'Español', short: 'ES' },
  { code: 'de', flag: <span>🇩🇪</span>, name: 'Deutsch', short: 'DE' },
  { code: 'pt', flag: <span>🇵🇹</span>, name: 'Português', short: 'PT' },
  { code: 'pl', flag: <span>🇵🇱</span>, name: 'Polski', short: 'PL' },
  { code: 'ja', flag: <span>🇯🇵</span>, name: '日本語', short: 'JA' },
]

const languages = languagesMeta.filter(
  (l): l is LanguageMeta & { code: SupportedLanguage } =>
    (supportedLanguages as readonly string[]).includes(l.code)
)

interface LanguageSelectorProps {
  variant?: 'desktop' | 'mobile'
  isScrolled?: boolean
  isLightTop?: boolean
}

export function LanguageSelector({
  variant = 'desktop',
  isScrolled = false,
  isLightTop = false,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const currentLang = (i18n.language || supportedLanguages[0]) as SupportedLanguage
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentLanguage =
    languages.find((l) => l.code === currentLang) || languages[0]

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  const switchLanguage = (newLang: SupportedLanguage) => {
    if (newLang === currentLang) {
      setIsOpen(false)
      return
    }

    // Translate the current path to the target language via localizedRoutes
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const currentPath = pathSegments.slice(1).join('/')

    let newPath = ''
    for (const [, routes] of Object.entries(localizedRoutes)) {
      if (routes[currentLang] === currentPath) {
        newPath = routes[newLang]
        break
      }
    }

    if (!newPath && currentPath) {
      newPath = currentPath
    }

    i18n.changeLanguage(newLang)
    navigate(`/${newLang}${newPath ? `/${newPath}` : ''}`)
    setIsOpen(false)
  }

  if (variant === 'mobile') {
    return (
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => switchLanguage(lang.code)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
              lang.code === currentLang
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >{lang.flag}{lang.name}</button>
        ))}
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
          isScrolled
            ? 'text-gray-900 hover:bg-gray-100'
            : isLightTop
              ? 'text-ink/90 hover:text-ink hover:bg-ink/10'
              : 'text-white/90 hover:bg-white/10'
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {currentLanguage.flag}
        <span>{currentLanguage.short}</span>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50"
          role="listbox"
          aria-activedescendant={`lang-${currentLang}`}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              id={`lang-${lang.code}`}
              role="option"
              aria-selected={lang.code === currentLang}
              onClick={() => switchLanguage(lang.code)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left',
                lang.code === currentLang
                  ? 'bg-gray-100 text-gray-900 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
            >
              {lang.flag}
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
